import type {
  Exam,
  MockScore,
  NoteInbox,
  Instant,
  Milestone,
  MogakSession,
  PhotoAsset,
  Plan,
  PlanItem,
  StudyLog,
  Subject,
  UUID,
  WrongNote,
} from '@hoedokloop/domain';
import Dexie, { type EntityTable } from 'dexie';

export type AppMetaRecord = { key: string; value: string | number | boolean; updatedAt: Instant };
export type SettingRecord = { key: string; value: unknown; updatedAt: Instant };
export type OutboxRecord = {
  id?: number;
  kind: 'event' | 'backup_hint' | 'subscription';
  payload: unknown;
  attempts: number;
  nextAttemptAt: Instant;
  createdAt: Instant;
};
export type ImportCheckpoint = {
  id: UUID;
  encryptedPayload: Uint8Array;
  status: 'created' | 'verified' | 'restored' | 'failed';
  createdAt: Instant;
};

export class HoedokLoopDatabase extends Dexie {
  exams!: EntityTable<Exam, 'id'>;
  mockScores!: EntityTable<MockScore, 'id'>;
  noteInbox!: EntityTable<NoteInbox, 'id'>;
  subjects!: EntityTable<Subject, 'id'>;
  studyLogs!: EntityTable<StudyLog, 'id'>;
  wrongNotes!: EntityTable<WrongNote, 'id'>;
  photoAssets!: EntityTable<PhotoAsset, 'id'>;
  plans!: EntityTable<Plan, 'id'>;
  planItems!: EntityTable<PlanItem, 'id'>;
  milestones!: EntityTable<Milestone, 'id'>;
  mogakSessions!: EntityTable<MogakSession, 'id'>;
  appMeta!: EntityTable<AppMetaRecord, 'key'>;
  settings!: EntityTable<SettingRecord, 'key'>;
  outbox!: EntityTable<OutboxRecord, 'id'>;
  importCheckpoints!: EntityTable<ImportCheckpoint, 'id'>;

  constructor(name = 'hoedokloop') {
    super(name);
    this.version(1).stores({
      exams: 'id, examDate, updatedAt, deletedAt',
      subjects: 'id, examId, [examId+sortOrder], updatedAt, deletedAt',
      studyLogs:
        'id, examId, subjectId, studyDate, [subjectId+round], [studyDate+subjectId], updatedAt, deletedAt',
      wrongNotes:
        'id, examId, subjectId, photoAssetId, *tags, lastReviewedAt, masteredAt, updatedAt, deletedAt',
      photoAssets: 'id, sha256, createdAt, deletedAt',
      plans: 'id, examId, [examId+version], status, generatedAt',
      planItems: 'id, planId, date, subjectId, [planId+date], [subjectId+round]',
      milestones: 'id, key, examId, sourceEntityId, [key+examId+sourceEntityId], earnedAt',
      mogakSessions: 'id, roomId, examId, joinedAt, outcome, savedLogId',
      appMeta: 'key, updatedAt',
      settings: 'key, updatedAt',
      outbox: '++id, kind, nextAttemptAt, createdAt',
      importCheckpoints: 'id, status, createdAt',
    });
    this.version(2).stores({
      mockScores: 'id, examId, studyDate, updatedAt, deletedAt',
      noteInbox: 'id, photoAssetId, createdAt, deletedAt',
    });
  }
}
